/**
 * Thin, replaceable text catalog (research phase 2 onward). The server and the database store only keys and parameters;
 * the text is looked up here (client, and server-side notification templates). Only `hu` exists today, but the shape is
 * multi-language: `Record<Locale, Catalog>`. Choosing the language is the job of the later i18n work.
 */
import { hu } from './hu.js';

export type Locale = 'hu';
export type Catalog = Record<string, string>;
export type Params = Record<string, string | number>;

export const CATALOGS: Record<Locale, Catalog> = { hu };
export const DEFAULT_LOCALE: Locale = 'hu';

/** `t('key', { name })`: whole sentences with `{param}` slots (no grammar is glued onto parameters). Unknown key: the key itself. */
export function t(key: string, params: Params = {}, locale: Locale = DEFAULT_LOCALE): string {
  const text = CATALOGS[locale]?.[key] ?? CATALOGS[DEFAULT_LOCALE][key];
  if (text === undefined) return key;
  return text.replace(/\{(\w+)\}/g, (_, k: string) => (k in params ? String(params[k]) : `{${k}}`));
}

/** A server-side message as it is stored/sent: a key plus parameters. */
export interface MessageRef { key: string; params?: Params }

/** Stored log/notification text: `{"key":…,"params":…}` is rendered through the catalog, older plain text is shown as it is. */
export function renderStored(text: string, locale: Locale = DEFAULT_LOCALE): string {
  if (text.startsWith('{"key"')) {
    try { const m = JSON.parse(text) as MessageRef; return t(m.key, m.params ?? {}, locale); } catch { return text; }
  }
  return text;
}
export const storeMessage = (key: string, params: Params = {}): string => JSON.stringify({ key, params });

// ---- Formatting through Intl (the language of the client, never hand-glued separators) ----
export const formatNumber = (n: number, locale: string = DEFAULT_LOCALE, digits = 0): string =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(n);
export const formatPercent = (fraction: number, locale: string = DEFAULT_LOCALE): string =>
  new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(fraction);
export const formatDateTime = (iso: string | number | Date, locale: string = DEFAULT_LOCALE): string =>
  new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));
